// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { _formatString } from '@internal/acs-ui-common';
import React, { useCallback, useState } from 'react';
import { MessageThreadStrings, UpdateMessageCallback } from '../../MessageThread';
import { ChatMessage, ComponentSlotStyle, OnRenderAvatarCallback } from '../../../types';
import { AttachmentMenuAction } from '../../../types/Attachment';
import { AttachmentMetadata } from '@internal/acs-ui-common';
import { InlineImageOptions } from '../ChatMessageContent';
import { ChatMyMessageComponentAsMessageBubble } from './ChatMyMessageComponentAsMessageBubble';
import { ChatMessageComponentAsEditBoxPicker } from './ChatMessageComponentAsEditBoxPicker';
/**
 * Props for the ChatMyMessageComponent component.
 */
export type ChatMyMessageComponentProps = {
  message: ChatMessage;
  userId: string;
  messageContainerStyle?: ComponentSlotStyle;
  showDate?: boolean;
  disableEditing?: boolean;
  onUpdateMessage?: UpdateMessageCallback;
  onCancelEditMessage?: (messageId: string) => void;
  /**
   * Callback to delete a message. Also called before resending a message that failed to send.
   * @param messageId ID of the message to delete
   */
  onDeleteMessage?: (messageId: string) => Promise<void>;
  /**
   * Callback to send a message
   * @param content The message content to send
   */
  onSendMessage?: (content: string) => Promise<void>;
  strings: MessageThreadStrings;
  messageStatus?: string;
  /**
   * Optional text to display when the message status is 'failed'.
   */
  failureReason?: string;
  /**
   * Whether the status indicator for each message is displayed or not.
   */
  showMessageStatus?: boolean;
  /**
   * Whether to overlap avatar and message when the view is width constrained.
   */
  shouldOverlapAvatarAndMessage: boolean;
  remoteParticipantsCount?: number;
  onActionButtonClick: (message: ChatMessage, setMessageReadBy: (readBy: {
    id: string;
    displayName: string;
  }[]) => void) => void;
  /**
   * Optional callback to override render of the avatar.
   *
   * @param userId - user Id
   */
  onRenderAvatar?: OnRenderAvatarCallback;
  /**
   * Optional callback called when an inline image is clicked.
   * @beta
   */
  inlineImageOptions?: InlineImageOptions;
  /**
   * Optional callback to render message attachments in the message component.
   * @beta
   */
  onRenderAttachmentDownloads?: (message: ChatMessage) => JSX.Element;
  /**
   * Optional callback to define custom actions for attachments.
   * @beta
   */
  actionsForAttachment?: (attachment: AttachmentMetadata, message?: ChatMessage) => AttachmentMenuAction[];
};

/**
 * @private
 */
export const ChatMyMessageComponent = (props: ChatMyMessageComponentProps): JSX.Element => {
  const {
    onDeleteMessage,
    onSendMessage,
    message,
    onCancelEditMessage,
    onUpdateMessage
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [focusMessageAfterEditing, setFocusMessageAfterEditing] = useState(false);
  const onEditClick = useCallback(() => {
    setIsEditing(true);
    setFocusMessageAfterEditing(false);
  }, []);
  const clientMessageId = 'clientMessageId' in message ? message.clientMessageId : undefined;
  const content = 'content' in message ? message.content : undefined;
  const onRemoveClick = useCallback(() => {
    if (onDeleteMessage && message.messageId) {
      onDeleteMessage(message.messageId);
    }
    // when fail to send, message does not have message id, delete message using clientMessageId
    else if (onDeleteMessage && message.messageType === 'chat' && clientMessageId) {
      onDeleteMessage(clientMessageId);
    }
  }, [onDeleteMessage, message.messageId, message.messageType, clientMessageId]);
  const onResendClick = useCallback(() => {
    onDeleteMessage && clientMessageId && onDeleteMessage(clientMessageId);
    let newContent = content ?? '';
    onSendMessage && onSendMessage(newContent);
  }, [message, onDeleteMessage, clientMessageId, onSendMessage, content]);
  const onSubmitHandler = useCallback(
  // due to a bug in babel, we can't use arrow function here
  // affecting conditional-compile-remove(attachment-upload)
  async function (text: string) {
    await onUpdateMessage?.(message.messageId, text);
    setIsEditing(false);
    setFocusMessageAfterEditing(true);
  }, [message, onUpdateMessage]);
  const onCancelHandler = useCallback((messageId: string) => {
    onCancelEditMessage?.(messageId);
    setIsEditing(false);
    setFocusMessageAfterEditing(true);
  }, [onCancelEditMessage]);
  if (isEditing && message.messageType === 'chat') {
    return <ChatMessageComponentAsEditBoxPicker message={message} strings={props.strings} onSubmit={onSubmitHandler} onCancel={onCancelHandler} />;
  } else {
    return <ChatMyMessageComponentAsMessageBubble {...props} onRemoveClick={onRemoveClick} onEditClick={onEditClick} onResendClick={onResendClick} onRenderAvatar={props.onRenderAvatar} strings={props.strings} inlineImageOptions={props.inlineImageOptions} shouldFocusFluentMessageBody={focusMessageAfterEditing} />;
  }
};